import GenderMap from '@/utils/GenderMap'
import { Disclosure, Transition } from '@headlessui/react'
import { PersonalizeURLBuilder } from '@/utils/URLBuilder'
import allAlerts from '@/config/alerts.json'
import { v4 as uuidv4 } from 'uuid'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Button from '../Button'
import { motion, AnimatePresence } from "framer-motion";
import { CartItem as Item } from '@/contexts/CartReducer'
import { useCartDispatch, useCartState } from '@/contexts/CartContext'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import language_translations from '@/config/language'
import { Dispatch, SetStateAction } from 'react'
import { addItem, removeItem } from 'CartActions'
import React from 'react'
import { usePersonalizeModal } from '@/contexts/PersonalizeModalContext';
import GetSlugs from '@/services/api/products/GetSlugs'
import { GetStaticProps } from 'next'
import GetBySlug from '@/services/api/products/GetBySlug'
import { GetSettings } from '@/services/api/settings'
import { GetDocumentInputs } from '@/services/api/personalize/GetDocuments'
import {ParsedUrlQuery} from 'querystring'
import { useQuery } from 'react-query'
import { useNotebookModal } from '@/contexts/NotebookModalContext'
import { GetDedicationImage } from '@/services/api/personalize/DedicationImage'
import {
  PriceIcon,
  EditIcon,
  DisclosureDeleteIcon,
  DisclosureDeleteProductIcon,
  DisclosureDuplicateIcon,
  DisclosureEditDetailsIcon,
  DisclosureEditIcon,
  DisclosureMenuCloseIcon,
  DisclosureMenuIcon,
  AngleDownIcon,
  MenuCrossIcon,
  CrossIcon
} from '@/components/Icons/CartItemIcons'
import { useTranslation } from 'next-i18next'
import { useCurrency } from '../DesktopHeader'
import useUserPreferences from '@/hooks/useUserPreferences'
import { useAIPersonalizeModal } from '@/contexts/AIPersonalizeModalContext';
import { track } from '@/lib/tracker'

interface IParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths = async () => {
  try {
    const arr: string[] = await GetSlugs()
    const paths = arr.map(slug => {
      return {
        params: {slug},
      }
    })
    return {paths, fallback: 'blocking'}
  } catch (error) {
    console.error('Error getting slugs:', error)
    return {paths: [], fallback: 'blocking'} 
  }
}

export const getStaticProps: GetStaticProps = async context => {
  const {slug} = context.params as IParams;
  const locale = context.locale ?? 'en'
  
  // Use default currency/country for static generation
  const defaultCurrency = 'ILS';
  const defaultCountryCode = 'IL';
  
  try {
    const [product, settings, inputs] = await Promise.all([
      GetBySlug(slug, defaultCurrency, defaultCountryCode),
      GetSettings(defaultCurrency, defaultCountryCode),
      GetDocumentInputs(slug),
    ]);
    return {props: {product, settings, inputs}, revalidate: 10};
  } catch (error) {
    console.error('Error fetching product data:', error);
    return {
      props: {
        product: null,
        settings: null,
        inputs: null,
      },
      revalidate: 10,
    };
  }
}

interface Props {
  item: Item
  index: number
  setIsNotebookOpen: Dispatch<SetStateAction<boolean>>,
  displayButtonsIcon: boolean,
  inputs?: string[]
}

export default function CartItemDisclosure({
  item,
  index,
  setIsNotebookOpen,
  displayButtonsIcon,
  inputs
}: Props) {
  const router = useRouter()
  const cart = useCartState()
  const preferences = useUserPreferences();

  const { data: settingsInCart, isLoading, error } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  )
  
  // Use user's currency preference or fallback to cart currency
  const preferencesCurrency = useCurrency(preferences?.currencyCode || cart.currency);
  const cartCurrency = useCurrency(cart.currency);

  const currencySymbol = preferences?.currencyCode ? 
    preferencesCurrency.symbol : 
    cartCurrency.symbol;
    
  const {t} = useTranslation('alerts');
  const {t: tCart} = useTranslation('cart');
  const {locale} = useRouter();
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts.ar;
  const [showPackagingInfo, setShowPackagingInfo] = React.useState(preferences?.countryCode === 'IL' ? true : false);
  
  const name='';
  const [showPreview, setShowPreview] = React.useState(false);
  const [signedUrl, setSignedUrl] = React.useState<string | null>(null);
  const [fetchingImage, setFetchingImage] = React.useState(false);
  
  const processedKey = localStorage.getItem(`dedicationImageUrl-${item.uuid}`);
  const aiStoryImageKey = localStorage.getItem(`aiStoryImage-${item.uuid}`);
  
  // AI story detection logic (same as in CartItem)
  const aiStoryData = React.useMemo(() => {
    try {
      const data = localStorage.getItem(`aiStoryData-${item.uuid}`);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading AI story data:', error);
      return null;
    }
  }, [item.uuid]);

  const isAIStory = React.useMemo(() => {
    // Check multiple indicators
    if (aiStoryData) return true;
    if (aiStoryImageKey) return true;
    
    // Check if product has AI story indicator
    if (item?.product?.is_ai === true) return true;
    
    // Check product slug or name for AI story indicators
    if (item.product_slug && item.product_slug.includes('ai-story')) return true;
    if (item.product_name && typeof item.product_name === 'string' && 
        (item.product_name.toLowerCase().includes('ai') || 
         item.product_name.toLowerCase().includes('story'))) return true;
    
    return false;
  }, [aiStoryData, aiStoryImageKey, item?.product?.is_ai, item.product_slug, item.product_name]);
  const getImageKeyForPreview = () => {
    // First check if we have a dedication image key
    if (processedKey) return processedKey;
    
    // If no dedication image but it's an AI story with AI image as dedication
    if (isAIStory && aiStoryImageKey) {
      return aiStoryImageKey;
    }
    
    return null;
  };

  const previewKey = getImageKeyForPreview();

  const {data: settings} = useQuery(['settings', preferences?.countryCode, preferences?.currencyCode], () => GetSettings(preferences?.countryCode, preferences?.currencyCode));
  
  React.useEffect(() => {                
    if (!previewKey) {
      setSignedUrl(null);
      return;
    }

    let isMounted = true;
    
    const fetchImage = async () => {
      setFetchingImage(true);
      try {
        // Try to fetch dedication image first
        if (previewKey) {
          const response = await GetDedicationImage(previewKey);
          const url = response?.data?.url;
          if (isMounted) setSignedUrl(url);
        } 
        // If no dedication image but has AI story image, try to get it
        else if (aiStoryImageKey) {
          const response = await GetDedicationImage(aiStoryImageKey);
          const url = response?.data?.url;
          if (isMounted) setSignedUrl(url);
        }
        
        setFetchingImage(false);
      } catch (error) {
        console.error("Error fetching image:", error);
        if (isMounted) setSignedUrl(null);
        setFetchingImage(false);
      } finally {
        setFetchingImage(false);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [item.uuid, previewKey, aiStoryImageKey]);

  const BuildInputValues = () => {
    const build_inputs: {[key: string]: string} = {}
    if (!inputs) return build_inputs;
    
    Object.keys(inputs).forEach((input) => {
      if (input === 'name' || item?.inputs?.name) {
        build_inputs[input] = name;
      } else if (input === 'first_name' || item?.inputs?.first_name) {
        build_inputs[input] = name.split(' ')[0] || '';
      } else if (input === 'init') {
        build_inputs[input] = name.charAt(0) || '';
      } else if (input === 'age') {
        build_inputs[input] = '';
      }
    });
    return build_inputs
  }
  
  const { openModal } = usePersonalizeModal()
  const { openModal: openNotebookModal } = useNotebookModal();
  const { openAIModal: openAIPersonalizeModal } = useAIPersonalizeModal();
  const cartDispatch = useCartDispatch()
  
  const duplicateItem = (item: Item) => {
    const newUuid = uuidv4();
    // Copy the stored image from localStorage
    const oldImageUrl = localStorage.getItem(`dedicationImageUrl-${item.uuid}`);
    if (oldImageUrl) {
      localStorage.setItem(`dedicationImageUrl-${newUuid}`, oldImageUrl);
    }

    // Copy AI story image if exists
    const oldAIStoryImageKey = localStorage.getItem(`aiStoryImage-${item.uuid}`);
    if (oldAIStoryImageKey) {
      localStorage.setItem(`aiStoryImage-${newUuid}`, oldAIStoryImageKey);
    }
    
    // Copy AI story data if exists
    const oldAIStoryData = localStorage.getItem(`aiStoryData-${item.uuid}`);
    if (oldAIStoryData) {
      localStorage.setItem(`aiStoryData-${newUuid}`, oldAIStoryData);
    }

    const new_item = {
      ...item,
      uuid: newUuid,
      image: item.image,
      is_ai: item?.product?.is_ai,
    };

    cartDispatch(addItem(new_item));

    toast.custom((tel) => (
      <CustomToast t={tel} message={t('cart_item_duplicated')} type="success" />
    ));
  };
  
  const deleteCartItem = async (id: string, product_id: string) => {
    await cartDispatch(removeItem(id));
    await track({ event_name: "remove_from_cart", metadata: {product_id: product_id} });
    toast.custom((t) => <CustomToast t={t} message={alerts.cart_item_removed} type='success' />);
  }

  const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);

  const demoName =
  typeof item?.product?.demo_name === 'object'
    ? item?.product?.demo_name?.[locale === 'ar' ? 'ar' : 'en']
    : item?.demo_name

  // Function to handle edit button click
  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (item.product_type === 1) {
      // Check if this is an AI story
      if (isAIStory) {
        // Open AI personalize modal for AI stories
        openAIPersonalizeModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1',
        });
      } else {
        // Open regular personalize modal for non-AI stories
        openModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1',
        });
      }
    } else if (item.product_type == 2) {
      openNotebookModal({
        product: {...item},
        inputs: item?.inputs,
        isEdit: '1'
      });
    }
  };

  // Function to handle submenu edit click
  const handleSubMenuEditClick = () => {
    setIsSubMenuOpen(false);
    
    if (item.product_type === 1) {
      if (isAIStory) {
        openAIPersonalizeModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1',
        });
      } else {
        openModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1'
        });
      }
    } else if (item.product_type === 2) {
      openNotebookModal({
        product: {...item},
        inputs: item?.inputs,
        isEdit: '1'
      });
    }
  };
  
  return (
    <div className="w-full">
      <div className="w-full rounded-2xl bg-white">
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button className={`relative ${locale === 'ar' ? 'flex': 'flex flex-row-reverse'} w-full py-2 px-2 items-center`}>
                <>
                  <div className={`w-full ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'}`} dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                    <div className="basis-1/4 ml-4">
                      <div className="cart-item-image ">
                        <Image
                          src={process.env.NEXT_PUBLIC_CDN_URL + '/' + item.image}
                          alt="product-image"
                          width={100}
                          height={100}
                          className="rounded-md w-full object-cover"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between flex-1">
                      <div>
                        <div className="flex items-center lg:hidden">
                          <div className="bg-red w-11 h-11 rounded-full hidden lg:flex items-center justify-center">
                            <PriceIcon/>
                          </div>
                          <h2 className="text-red text-xl font-semibold">
                            <div className="flex flex-col items-center justify-center vs:text-lg sm:text-2xl">
                              <p>{currencySymbol}{item.total}</p>
                            </div>
                          </h2>
                        </div>
                        <div className="cart-item-content w-full">
                          <h1 className={`font-semibold vs:my-0 sm:my-2 text-primary ${locale === 'ar' ? 'text-right' : 'text-left'} text-base vs:text-md`}>
                            {demoName ? demoName.replace('{basmti}', item?.replace_name) : ''}
                            {isAIStory && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                {tCart('aiStoryLabel')}
                              </span>
                            )}
                          </h1>
                        </div>
                        <div className="w-full flex items-center justify-start" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                          <div className={`flex items-center text-gray-500 text-xs vs:text-[8px] mt-1 underline underline-offset-2`}>
                            <span className={`ml-1`}>{tCart('viewCartItemDetails')}</span>
                            {open ? (
                              <AngleDownIcon/>
                            ) : (
                              <span className='rotate-180'><AngleDownIcon/></span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {displayButtonsIcon && (
                    <button 
                      className={`py-1 hover:bg-gray-100 active:bg-gray-200 absolute top-0 ${locale === 'ar' ? 'left-0': 'right-0'} md:hidden mt-4`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSubMenuOpen(!isSubMenuOpen);
                      }}
                    >
                      { !isSubMenuOpen ? (
                        <MenuCrossIcon/>
                      ) : (
                        <DisclosureMenuIcon/>
                      )}
                    </button>
                  )}

                  {showPreview && (
                    <div className="fixed inset-0 z-[999] bg-black bg-opacity-70 flex items-center justify-center">
                      <div className="relative bg-white rounded-lg shadow-lg max-w-[40%] max-h-[80vh] p-4 overflow-hidden">
                        <button
                          onClick={() => setShowPreview(false)}
                          className={`absolute top-2 ${locale === 'ar' ? 'right-2' : 'left-2'} text-primary w-12 h-12`}
                        >
                          <CrossIcon />
                        </button>

                        {fetchingImage ? (
                          <div className="flex flex-col items-center justify-center p-6 mt-6">
                            <div className="w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                            <p className="text-sm text-gray-600 mt-3">{tCart('loadingPreviewText')}</p>
                          </div>
                        ) : signedUrl ? (
                          <ImageWithLoader src={signedUrl} />
                        ) : (
                          <p className="text-sm text-gray-600 mt-6">{tCart('unableToLoadThePreview')}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <AnimatePresence>
                    {isSubMenuOpen && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`absolute top-0 ${locale === 'ar' ? 'left-0': 'right-0'} bg-white shadow-md p-4 z-10 w-36 rounded-md`}
                      >
                        <div className={`flex ${locale === 'ar' ? 'justify-end' : 'justify-start'} mb-4`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsSubMenuOpen(false);
                            }}
                            className="text-gray-600 hover:text-black"
                          >
                            <DisclosureMenuCloseIcon/>
                          </button>
                        </div>

                        <button
                          onClick={handleSubMenuEditClick}
                          className="flex items-center justify-center w-full py-1 px-1 text-xs rounded hover:bg-gray-300 border border-primary-400 text-primary"
                        >
                          <span className="mx-2">
                            <DisclosureEditIcon/>
                          </span>
                          {tCart('cartItemdisclosureEditText')}
                        </button>

                        <hr className="my-2" />

                        <button
                          onClick={() => {
                            setIsSubMenuOpen(!isSubMenuOpen);
                            duplicateItem(item);
                          }}
                          className="flex items-center justify-center w-full py-1 px-1 text-xs rounded hover:bg-gray-300 border border-primary-400 text-primary"
                        >
                          <span className="mx-1">
                            <DisclosureDuplicateIcon/>
                          </span>
                          {tCart('cartItemDisclosureDuplicateText')}
                        </button>

                        <hr className="mt-2 mb-1" />

                        <button
                          onClick={() => {
                            setIsSubMenuOpen(!isSubMenuOpen);
                            deleteCartItem(item.uuid, item?.product?.id);
                          }}
                          className="flex items-center justify-center w-full py-1 px-1 text-sm rounded hover:bg-gray-300 text-red-700"
                        >
                          <span className="mx-2">
                            <DisclosureDeleteIcon/>
                          </span>
                          {tCart('cartItemDisclosureDeleteText')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              </Disclosure.Button>
              
              <Transition
                show={open}
                enter="transition-all duration-600 ease-in-out"
                enterFrom="opacity-0 max-h-0"
                enterTo="opacity-100 max-h-[1000px]"
                leave="transition-all duration-200 ease-in-out"
                leaveFrom="opacity-100 max-h-[1000px]"
                leaveTo="opacity-0 max-h-0"
              >
                <Disclosure.Panel className="text-text-color leading-[2rem] px-4 text-[15px] pb-4">
                  <div className='basis-4/12 mr-4"'>
                    {item['product_type'] ? (
                      <div className={`cart-item-content gap-1 w-full ${locale === 'ar' ? 'lg:mr-4' : 'lg:ml-4'}`} dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                        {item.product_type == 2 && (
                          <>
                            <div className="flex gap-1 items-center">
                              <p className="text-gray-400">{tCart('cartItemDetailsChildName')}</p>
                              <p className="text-primary lg:mr-2">{item?.replace_name}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                              <p className="text-gray-400">{tCart('cartItemDetailsNotebookType')}</p>
                              <p className="text-primary lg:mr-2">
                                {item.language ? language_translations[item.language] : ''}
                              </p>
                            </div>

                            <div className="flex gap-1 items-center">
                              <p className="text-gray-400">{tCart('cartItemDetailsNotebookQuantity')}</p>
                              <p className="text-primary lg:mr-2 text-sm">
                                {item.quantity ? item.quantity : 0} {tCart('cartItemDetailsNotebookQuantityText')}
                              </p>
                            </div>
                           
                            {item.product_type == 2 && (previewKey) && (
                              <div className="lg:flex">
                                <p className="text-gray-400">{tCart('cartItemDetailsChildImage')}</p>
                                <div className="text-primary lg:mr-2">
                                  <button
                                    onClick={() => setShowPreview(true)}
                                    className="text-primary underline underline-offset-8 hover:text-secondary transition-colors"
                                  >
                                    {tCart('cartItemDetailsImagePreviewText')} ({settings?.image_price?.symbol} {settings?.image_price?.amount})
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        
                        {item.product_type == 1 && (
                          <>
                            <div className="lg:flex gap-1 items-center">
                              <p className="text-gray-400">{tCart('cartItemDetailsChildName')}</p>
                              <p className="font-medium lg:mr-2">
                                {item?.replace_name} ({GenderMap(item.gender ?? '', locale)})
                              </p>
                            </div>

                            <div className="lg:flex gap-1 items-center">
                              <p className="text-gray-400">{tCart('cartItemDetialsDedication')}</p>
                              <p className="lg:mr-2 leading-normal font-light">
                                {Array.from(item.dedication || "").slice(0, 100).join("")}
                                {item.dedication && item.dedication.length > 100 ? "..." : ""}
                              </p>
                            </div>

                            <div className="lg:flex gap-1 items-center mt-1">
                              <p className="text-gray-400">{tCart('cartItemDetialsCover')}</p>
                              <p className="text-primary lg:mr-2">
                                {item.cover?.name ? 
                                  (typeof item.cover.name === 'string' ? 
                                    item.cover.name : 
                                    (locale === 'ar' ? item.cover.name?.ar : item.cover.name?.en)
                                  ) : ''
                                } ({
                                  item.cover?.price ? 
                                    (typeof item.cover.price === 'string' ? 
                                      item.cover.price : 
                                      item.cover.price?.amount
                                    ) : ''
                                }{currencySymbol})
                              </p>
                            </div>
                            
                            {item.product_type == 1 && showPackagingInfo && (
                              <div className="lg:flex">
                                <p className="text-gray-400">{tCart('cartItemDetialsPackaging')}</p>
                                <p className="text-primary lg:mr-2">
                                  {item.packaging?.name === 'Packaging' ? `${tCart('packagingAdded')}` : `${tCart('packagingNotAdded')}`} ({currencySymbol} {item.packaging?.price ?? 0})
                                </p>
                              </div>
                            )}
                            
                            {item.product_type == 1 && (
                              <div className="lg:flex">
                                <p className="text-gray-400">{tCart('cartItemDetailsChildImage')}</p>
                                <div className="text-primary lg:mr-2">
                                  {(previewKey) ? (
                                    <button
                                      onClick={() => setShowPreview(true)}
                                      className="text-primary underline underline-offset-8 hover:text-secondary transition-colors"
                                    >
                                      {tCart('cartItemDetailsImagePreviewText')} ({settings?.image_price?.symbol} {settings?.image_price?.amount})
                                    </button>
                                  ) : (
                                    <span>{tCart('cartItemDetailsImageNotAdded')} ({currencySymbol} 0)</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className={`cart-item-content w-full ${locale === 'ar' ? 'lg:mr-4' : 'lg:ml-4'}`} dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                        <div className="lg:flex gap-1 items-center">
                          <p className="text-gray-400">{tCart('cartItemDetailsChildName')}</p>
                          <p className="font-medium lg:mr-2">
                            {item?.replace_name} ({GenderMap(item.gender ?? '', locale)})
                          </p>
                        </div>
                        <div className="mt-3 lg:flex gap-1 items-center">
                          <p className="text-gray-400">{tCart('cartItemDetialsDedication')}</p>
                          <p className="text-primary lg:mr-2">
                            {Array.from(item.dedication || "").slice(0, 100).join("")}
                            {item.dedication && item.dedication.length > 100 ? "..." : ""}
                          </p>
                        </div>
                        <div className="mt-3 lg:flex gap-1 items-center">
                          <p className="text-gray-400">{tCart('cartItemDetialsCover')}</p>
                          <p className="text-primary lg:mr-2">
                            {item.cover?.name ? 
                              (typeof item.cover.name === 'string' ? 
                                item.cover.name : 
                                (locale === 'ar' ? item.cover.name?.ar : item.cover.name?.en)
                              ) : ''
                            } ({
                              item.cover?.price ? 
                                (typeof item.cover.price === 'string' ? 
                                  item.cover.price : 
                                  item.cover.price?.amount
                                ) : ''
                            }{currencySymbol})
                          </p>
                        </div>
                        {showPackagingInfo && (
                          <div className="mt-3 lg:flex gap-1 items-center">
                            <p className="text-gray-400">{tCart('cartItemDetialsPackaging')}</p>
                            <p className="text-primary lg:mr-2">
                              {item.packaging?.name === 'Packaging' ? `${tCart('packagingAdded')}` : `${tCart('packagingNotAdded')}`} ({currencySymbol} {item.packaging?.price ?? 0})
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {item['product_type'] ? (
                    <div className={`${displayButtonsIcon ? 'hidden': 'buttons'} lg:flex flex-row-reverse justify-between`}>
                      <Button
                        style="outline"
                        type="button"
                        bg="primary"
                        classes={[
                          'w-full',
                          'flex',
                          'mt-4',
                          'text-sm',
                          'justify-center',
                          'lg:mr-4',
                          'items-center',
                          'py-3',
                        ]}
                        onClick={handleEditClick}
                      >
                        <>
                          <span className="ml-2">
                            <DisclosureEditDetailsIcon/>
                          </span>
                          {tCart('editButtonText')}
                        </>
                      </Button>

                      <div className={`${displayButtonsIcon ? 'hidden': 'flex'} items-center gap-4 w-full mt-4`}>
                        <Button
                          type="button"
                          bg="primary"
                          onClick={() => duplicateItem(item)}
                          classes={[
                            'p-3',
                            'h-12',
                            'flex',
                            'items-center',
                            'justify-center',
                            'w-2/3'
                          ]}
                        >
                          {`${tCart('duplicateButtonText')}`}
                        </Button>
                        
                        <Button
                          type="button"
                          bg="primary"
                          onClick={() => deleteCartItem(item.uuid, item?.product?.id)}
                          classes={[
                            'w-1/3',
                            'border-2',
                            'border-red',
                            'flex-1',
                            'p-3',
                            'h-12',
                            'flex',
                            'items-center',
                            'justify-center',
                            'bg-white',
                            'hover:bg-red-600'
                          ]}
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                stroke="red"
                                strokeWidth="1"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="text-red">
                              {tCart('cartItemDisclosureDeleteText')}
                            </span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="buttons lg:flex flex-row-reverse justify-between">
                      <Button
                        type="button"
                        bg="primary"
                        onClick={() => duplicateItem(item)}
                        classes={['w-full', 'mt-4', 'lg:mr-4', 'text-sm', 'py-3']}
                      >
                        {`${tCart('duplicateButtonText')}`}
                      </Button>
                      
                      <Button
                        style="outline"
                        type="button"
                        bg="primary"
                        classes={[
                          'w-full',
                          'flex',
                          'mt-4',
                          'text-sm',
                          'justify-center',
                          'lg:mr-4',
                          'items-center',
                          'py-2',
                        ]}
                        onClick={handleEditClick}
                      >
                        <>
                          <span className="ml-2">
                            <DisclosureEditDetailsIcon/>
                          </span>
                          {tCart('editButtonText')}
                        </>
                      </Button>
                      
                      <div className="flex items-center  mt-4 lg:block lg:mt-4 w-full">
                        <div className="cart-item-mob lg:hidden">
                          <button
                            onClick={() => deleteCartItem(item.uuid, item?.product?.id)}
                            className="bg-white w-11 h-11 rounded-full flex items-center justify-center"
                          >
                            <DisclosureDeleteProductIcon/>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Disclosure.Panel>
              </Transition>
            </>
          )}
        </Disclosure>
      </div>
    </div>
  )
}

function ImageWithLoader({ src }: { src: string }) {
  const [loading, setLoading] = React.useState(true);
  const {t:tCart} = useTranslation('cart');

  return (
    <div className="relative w-full h-full flex items-center justify-center mt-12">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600 mt-3">{tCart('imageLoadingText')}</p>
        </div>
      )}
      <img
        src={src}
        alt="Product Preview"
        className={`w-full h-full object-contain rounded-md transition-opacity duration-300 ${
          loading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => setLoading(false)}
        onError={(e) => {
          console.error("Image failed to load:", e);
          setLoading(false);
        }}
      />
    </div>
  );
}